import { el, clear } from './dom.js';
import { icon } from './icons.js';
import { KEYS, SKIP, shortLabel, splitHint } from './actions.js';
import { t } from '../../shared/i18n.js';

// Phone and tablet controls: a stick for the left thumb, buttons for the
// right, and dragging on the view to look around. The buttons are made from
// the same hints a keyboard player reads, so they always match what can be
// done at that moment.
export class Touch {
  constructor(root, input, hud) {
    this.input = input;
    this.knob = el('i');
    this.stick = el('div', { id: 'stick', 'aria-label': t('Move') }, this.knob);
    this.buttons = el('div', { id: 'pad' });
    // always there on a phone: the phone, the map and the chat
    this.fixed = el('div', { id: 'padfixed' }, this.tap(t('Phone'), 'i', 'phone'), this.tap(t('Map'), 'Tab', 'map'), this.tap(t('Chat'), 'Enter', 'chat'));
    root.append(this.stick, this.buttons, this.fixed);
    document.body.classList.add('touch');
    this.id = null;
    this.key = '';

    const R = 46; // how far the knob travels, in pixels
    const at = (t) => {
      const box = this.stick.getBoundingClientRect();
      let dx = t.clientX - (box.left + box.width / 2);
      let dy = t.clientY - (box.top + box.height / 2);
      const len = Math.hypot(dx, dy);
      if (len > R) {
        dx = (dx / len) * R;
        dy = (dy / len) * R;
      }
      this.knob.style.transform = `translate(${dx}px, ${dy}px)`;
      input.stick.x = dx / R;
      input.stick.z = -dy / R;
    };
    const let_go = () => {
      this.id = null;
      this.knob.style.transform = '';
      input.stick.x = 0;
      input.stick.z = 0;
    };
    this.stick.addEventListener('touchstart', (e) => {
      e.preventDefault();
      const t = e.changedTouches[0];
      this.id = t.identifier;
      at(t);
    }, { passive: false });
    this.stick.addEventListener('touchmove', (e) => {
      e.preventDefault();
      for (const t of e.changedTouches) if (t.identifier === this.id) at(t);
    }, { passive: false });
    for (const type of ['touchend', 'touchcancel']) {
      this.stick.addEventListener(type, (e) => {
        for (const t of e.changedTouches) if (t.identifier === this.id) let_go();
      });
    }

    hud.onHint = (parts) => this.show(parts);
    if (hud.hintParts) this.show(hud.hintParts);
  }

  // A button that acts as a key: held while the finger is on it.
  button(label, key, wide) {
    const b = el(`button${wide ? '.wide' : ''}`, { type: 'button' }, label);
    const down = (e) => {
      e.preventDefault();
      if (key === 'click') this.input.clicked = true;
      else this.input.press(key);
      b.classList.add('on');
    };
    const up = (e) => {
      e.preventDefault();
      if (key !== 'click') this.input.release(key);
      b.classList.remove('on');
    };
    b.addEventListener('touchstart', down, { passive: false });
    b.addEventListener('touchend', up, { passive: false });
    b.addEventListener('touchcancel', up, { passive: false });
    return b;
  }

  // A tap button: pressed and released at once (opens something rather than holds a key).
  tap(label, key, iconName) {
    const b = el('button.fixed', { type: 'button', 'aria-label': label }, icon(iconName, 20), el('span', null, label));
    b.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.input.press(key);
      setTimeout(() => this.input.release(key), 120);
    }, { passive: false });
    return b;
  }

  // parts: the keyboard hints, alternating key and what it does. The actions that apply here
  // come first and larger; the rest follow smaller.
  show(parts) {
    const key = parts.join('|');
    if (key === this.key) return;
    this.key = key;
    clear(this.buttons);
    const text = shortLabel;
    const { primary, rest } = splitHint(parts);
    const ordered = [...primary.map((p) => [...p, true]), ...rest.map((p) => [...p, false])];
    for (const [name, does, main] of ordered) {
      if (SKIP.has(does)) continue;
      if (name === 'I' || name === 'Tab' || (name === 'Enter' && !/^land/.test(does))) continue; // the fixed row has these
      let b = null;
      if (name === 'Shift') b = this.button(text(does), 'Shift');
      else if (name === 'Space') b = this.button(text(does), ' ');
      else if (name === 'Click') b = this.button(text(does), 'click', true);
      else if (name === 'Q E') this.buttons.append(this.button('Down', 'q'), this.button('Up', 'e'));
      else if (KEYS[name]) b = this.button(text(does), KEYS[name]);
      if (b) {
        if (main) b.classList.add('main');
        this.buttons.append(b);
      }
    }
    // on foot there is no hint for jumping, and the build kit has no number keys here
    if (parts.includes('W A S D') && parts.includes('walk')) this.buttons.append(this.button(t('Jump'), ' '));
    if (parts.includes('1-9')) this.buttons.append(this.button('‹ Piece', '['), this.button('Piece ›', ']'));
  }
}
